

import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  CalendarDays,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";

interface EventType {
  id: number;
  name: string;
  status: string;
  created_at?: string;
}

// Reusable Action Button Component
function ActionButton({
  onClick,
  icon: Icon,
  label,
  variant = "default",
}: {
  onClick: () => void;
  icon: any;
  label: string;
  variant?: "default" | "primary" | "danger";
}) {
  const colors = {
    default: "text-stone-600 hover:bg-stone-100",
    primary: "text-amber-600 hover:bg-amber-50",
    danger: "text-red-600 hover:bg-red-50",
  };
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${colors[variant]}`}
      title={label}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function EventTypeForm({
  initialData,
  onSubmit,
  onCancel,
}: {
  initialData?: Partial<EventType>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialData?.name || "");
  const [status, setStatus] = useState(initialData?.status || "active");

  // Reset form when initialData changes (for new entries)
  useEffect(() => {
    if (!initialData?.id) {
      setName("");
      setStatus("active");
    } else {
      setName(initialData.name || "");
      setStatus(initialData.status || "active");
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, status });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Event Type Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Wedding, Corporate, Birthday"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData?.id ? "Update" : "Add"} Event Type
        </button>
      </div>
    </form>
  );
}

export default function EventTypesPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';

  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<EventType | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingType, setDeletingType] = useState<EventType | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await mastersApi.eventTypes.list();
      setEventTypes(response.data?.data || response.data || []);
    } catch (error) {
      showToast("Failed to load event types", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredTypes = useMemo(() => {
    if (!searchQuery) return eventTypes;
    const q = searchQuery.toLowerCase();
    return eventTypes.filter((t) => t.name.toLowerCase().includes(q));
  }, [eventTypes, searchQuery]);

  const handleDelete = async () => {
    if (!deletingType) return;
    setDeleteLoading(true);
    try {
      await mastersApi.eventTypes.delete(deletingType.id);
      showToast("Event type deleted successfully", "success");
      setDeleteModalOpen(false);
      setDeletingType(null);
      loadData(); // Reload data to ensure consistency
    } catch (error: any) {
      const errorMessage = error.message || "Failed to delete event type";
      showToast(errorMessage, "error");
      setDeleteModalOpen(false);
      setDeletingType(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editingType) {
        // Update existing
        await mastersApi.eventTypes.update(editingType.id, data);
        showToast("Event type updated successfully", "success");
      } else {
        // Create new
        await mastersApi.eventTypes.create(data);
        showToast("Event type created successfully", "success");
      }
      
      // Close modal and reset form
      setIsModalOpen(false);
      setEditingType(null);
      loadData(); // Reload to get fresh data
    } catch (error) {
      showToast("Failed to save event type", "error");
      loadData();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Event Types</h1>
        <p className="text-sm text-stone-500">Manage event type categories used across enquiries, quotations, and events</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search event types..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        {!isStaff && (
          <button
            onClick={() => { setEditingType(null); setIsModalOpen(true); }}
            className="px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Type
          </button>
        )}
      </div>

      {filteredTypes.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <CalendarDays className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No event types found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Name</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredTypes.map((type) => (
                  <tr key={type.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3 font-medium text-stone-800">{type.name}</td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`text-xs px-2 py-1 rounded-lg font-medium ${
                          type.status === "active"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-stone-100 text-stone-500"
                        }`}
                      >
                        {type.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {!isStaff && (
                        <div className="flex justify-end gap-1">
                          <ActionButton 
                            onClick={() => { setEditingType(type); setIsModalOpen(true); }} 
                            icon={Pencil} 
                            label="Edit" 
                          />
                          <ActionButton 
                            onClick={() => { setDeletingType(type); setDeleteModalOpen(true); }} 
                            icon={Trash2} 
                            label="Delete" 
                            variant="danger" 
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingType(null); }}
        title={editingType ? "Edit Event Type" : "Add New Event Type"}
        size="md"
      >
        <EventTypeForm
          initialData={editingType || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingType(null); }}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingType(null); }}
        onConfirm={handleDelete}
        title="Delete Event Type"
        message="Are you sure you want to delete this event type? This action cannot be undone."
        itemName={deletingType?.name}
        loading={deleteLoading}
      />
    </DashboardLayout>
  );
}
